/* ==================================================================
 * Central Firebase initialisation.
 *  - Single app instance (guards against duplicate init / HMR).
 *  - Validates required env vars up-front with a clear error.
 *  - Exports typed `auth`, `db`, `storage` singletons.
 * ================================================================== */
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import {
  initializeAuth,
  getAuth,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  browserLocalPersistence,
  inMemoryPersistence,
  type Auth,
} from 'firebase/auth'
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore'
import { getStorage, type FirebaseStorage } from 'firebase/storage'

interface FirebaseEnv {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

const REQUIRED_KEYS: Array<keyof FirebaseEnv> = [
  'apiKey',
  'authDomain',
  'projectId',
  'storageBucket',
  'messagingSenderId',
  'appId',
]

function readEnv(): FirebaseEnv {
  const env = import.meta.env
  return {
    apiKey: env.VITE_FIREBASE_API_KEY ?? '',
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: env.VITE_FIREBASE_PROJECT_ID ?? '',
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: env.VITE_FIREBASE_APP_ID ?? '',
  }
}

const config = readEnv()

/** Names of env vars that are still blank — used for the setup screen. */
export const missingFirebaseEnv: string[] = REQUIRED_KEYS.filter(
  (k) => !config[k] || config[k].trim() === '',
).map((k) => `VITE_FIREBASE_${k.replace(/([A-Z])/g, '_$1').toUpperCase()}`)

export const isFirebaseConfigured = missingFirebaseEnv.length === 0

/**
 * The internal domain used to turn a username into a synthetic email so we
 * can use Firebase Auth (email/password) under a username/password UX.
 */
export const AUTH_EMAIL_DOMAIN: string =
  import.meta.env.VITE_AUTH_EMAIL_DOMAIN || 'crickethub.local'

export function usernameToEmail(username: string): string {
  return `${username.trim().toLowerCase()}@${AUTH_EMAIL_DOMAIN}`
}

/**
 * Initialise eagerly only when configured. The app shell renders a friendly
 * setup screen when `isFirebaseConfigured` is false, so service code (which
 * only ever calls these inside functions) is never reached unconfigured.
 * We intentionally avoid a Proxy here — Firebase's modular SDK does internal
 * instance checks that a Proxy would break.
 */
let app: FirebaseApp | undefined
let authInstance: Auth
let dbInstance: Firestore
let storageInstance: FirebaseStorage

if (isFirebaseConfigured) {
  app = getApps().length ? getApp() : initializeApp(config)
  // App Check is OPT-IN and inert unless a reCAPTCHA v3 site key is provided at build time
  // (`VITE_APPCHECK_SITE_KEY`, a key registered under Firebase console → App Check → Apps, NOT the
  // Tournament-Admin reCAPTCHA key). With no key this block does nothing and requests carry no App
  // Check token, exactly as before. It must run before auth/Firestore are first used so their very first
  // requests carry a token — only matters once enforcement is switched on in the console, which must
  // happen AFTER this has shipped (see MIGRATIONS.md "App Check"). Never throws: a failure here must not
  // take the app down; the worst case is requests without a token.
  const appCheckKey = (import.meta.env.VITE_APPCHECK_SITE_KEY ?? '').trim()
  if (appCheckKey) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(appCheckKey),
        isTokenAutoRefreshEnabled: true,
      })
    } catch (e) {
      console.warn('App Check could not be initialised', e)
    }
  }
  // `initializeAuth` (not `getAuth`) with an explicit persistence list and NO
  // `popupRedirectResolver`: auth here is username/password over Firebase
  // email/password only — the app never calls signInWithPopup/Redirect or phone
  // auth. `getAuth()` would eagerly wire up the popup-redirect resolver, which
  // pulls in `https://apis.google.com/js/api.js` (the gapi iframe) on first
  // auth-state resolution — a script this app never uses, and one the site CSP
  // (correctly) blocks, so it logged a CSP violation to the console on every load.
  try {
    authInstance = initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence],
    })
  } catch {
    // Already initialised (HMR / double-eval) — fall back to the existing instance.
    authInstance = getAuth(app)
  }
  // Offline-first: IndexedDB persistent cache queues writes while offline and
  // syncs them (in order) on reconnect — this is what powers offline scoring.
  // Falls back to in-memory cache if the browser blocks IndexedDB.
  try {
    dbInstance = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    })
  } catch {
    dbInstance = getFirestore(app)
  }
  storageInstance = getStorage(app)
  // Local end-to-end testing against the Firebase Emulator Suite. `import.meta.env.DEV` is a
  // compile-time constant, so this whole block is removed from production builds; it can never
  // redirect a deployed app to an emulator.
  if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === '1') {
    connectAuthEmulator(authInstance, 'http://127.0.0.1:9099', { disableWarnings: true })
    connectFirestoreEmulator(dbInstance, '127.0.0.1', 8080)
  }
  // Default is 2 minutes. Image hosting has moved to R2 and a Storage bucket without a
  // CORS rule for this origin (local dev, or a post-migration deploy) makes every
  // `listAll`/`getDownloadURL` fail — with the default, the SDK retries that failure for
  // two minutes, logging a CORS error each attempt. Capping it keeps a genuinely broken
  // Storage call to a short, quiet failure while still tolerating a transient blip.
  storageInstance.maxOperationRetryTime = 8000
} else {
  // Unconfigured: leave as undefined-cast singletons. Access is gated by the
  // setup screen, so these are never used until real config is provided.
  authInstance = undefined as unknown as Auth
  dbInstance = undefined as unknown as Firestore
  storageInstance = undefined as unknown as FirebaseStorage
}

export const auth = authInstance
export const db = dbInstance
export const storage = storageInstance
export { app }
